import { array, magma, record, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { Optional } from '@mews/std';

const isValueUniformAmongEntities = <T, K extends keyof T>(entities: T[], key: K): boolean =>
    entities.length === 1 ||
    pipe(
        entities,
        array.map((entity) => String(entity[key] ?? string.empty)),
        array.uniq(string.Eq),
        array.size,
        (num) => num <= 1
    );

export const separateByUniformValues = <T, K extends keyof T>(entities: T[], keys: K[]) =>
    pipe(
        keys,
        array.partition((key) => isValueUniformAmongEntities(entities, key))
    );

export const prepareBulkEditValues = <T extends Record<string, Optional<string | string[]>>>({
    values,
    initialFormValues,
    unlockedFields,
}: {
    values: T;
    initialFormValues: T;
    unlockedFields?: Array<keyof T>;
}) =>
    Object.entries(values).reduce<Record<string, { value: string | string[] }>>((acc, [key, value]) => {
        const initialValue = initialFormValues[key];
        const isValueArray = Array.isArray(value);

        const hasValueChanged = isValueArray
            ? value.length !== initialFormValues?.[key]?.length ||
              pipe(value, array.difference(string.Eq)(initialValue as string[])).length > 0
            : value !== initialValue;

        const isFieldUnlocked = unlockedFields?.includes(key);

        if (hasValueChanged || isFieldUnlocked) {
            acc[key] = getEditValue(value);
        }

        return acc;
    }, {});

export const getEditValue = <V>(value: Optional<V>) => {
    if (value === '' || value == null) {
        return {
            value: '',
        };
    }

    return { value };
};

const mergeValuesMagma: magma.Magma<unknown> = {
    concat: (defaultValue, initialValue) => initialValue ?? defaultValue,
};
const mergeValues = record.union(mergeValuesMagma);
export const mergeWithDefaultValues = <V extends Record<string, unknown>>(initialValues: V, defaultValues: V) =>
    mergeValues(initialValues)(defaultValues);
